import { useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { log } from 'src/services/app/log.service'
import { CACHE_ACTIONS } from 'src/services/consts/app.const'
import { FCM_PENDING_PREFIXES } from 'src/services/consts/fcm.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { useAccountStore } from 'src/stores/account'
import { useCompaniesDirectory } from 'src/composables/account/query/useCompaniesDirectory'
import { approvalQueryFn } from 'src/composables/approval/query/useApprovalQuery'
import { useFcmPendingUpdates } from 'src/composables/fcm/shared/useFcmPendingUpdates'
import { ticketQueryFn } from 'src/composables/ticket/query/useTicketQuery'

export function useFcmCache() {
  const companyDictionary = useCompaniesDirectory()
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()
  const fcmPending = useFcmPendingUpdates()
  const router = useRouter()

  //============== Message handlers ==============
  function addMessageToCache({ ticketId, message }) {
    const messagesQueryKey = [QUERY_KEYS.MESSAGES, QUERY_KEYS.TICKET, ticketId]
    const hasQueryData = !!queryClient.getQueryData(messagesQueryKey)
    if (!hasQueryData) {
      log.info('[FCM] Message not added to cache - no query data')
      return
    }

    queryClient.setQueryData(messagesQueryKey, (oldMessages) => {
      if (!oldMessages) return [message]
      const exists = oldMessages.some((m) => m.messageId === message.messageId)
      if (exists) return oldMessages
      return [message, ...oldMessages]
    })
    log.info('[FCM] Message added to cache (viewing messages)')
  }

  function removeMessageFromCache({ ticketId, messageId }) {
    const messagesQueryKey = [QUERY_KEYS.MESSAGES, QUERY_KEYS.TICKET, ticketId]
    const hasQueryData = !!queryClient.getQueryData(messagesQueryKey)
    if (!hasQueryData) {
      log.info('[FCM] Message not removed from cache - no query data')
      return
    }
    queryClient.setQueryData(messagesQueryKey, (oldMessages) => {
      if (!oldMessages) return oldMessages
      return oldMessages.filter((m) => m.messageId !== messageId)
    })
    log.info('[FCM] Message removed from cache (viewing messages)')
  }

  //============== Ticket handlers ==============
  function setTicketInCache(ticketData) {
    if (!ticketData?.ticketId) return
    const ticketId = ticketData.ticketId
    queryClient.setQueryData([QUERY_KEYS.TICKET, ticketId], ticketData)
    if (ticketData.userMessages) {
      queryClient.setQueryData(
        [QUERY_KEYS.MESSAGES, QUERY_KEYS.TICKET, ticketId],
        ticketData.userMessages
      )
    }
    const accountData = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (accountData?.tickets) {
      const exists = accountData.tickets.some((t) => t.ticketId === ticketId)
      accountStore.updateAccountTickets(
        exists ? CACHE_ACTIONS.UPDATE : CACHE_ACTIONS.ADD,
        ticketData
      )
    }
  }

  function removeTicketDetailQueries(ticketId) {
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.TICKET, ticketId] })
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.MESSAGES, QUERY_KEYS.TICKET, ticketId],
    })
  }

  function removeTicketFromCache(ticketId) {
    if (!ticketId) return
    removeTicketDetailQueries(ticketId)
    accountStore.updateAccountTickets(CACHE_ACTIONS.REMOVE, { ticketId })
  }

  async function fetchTicketData(ticketId) {
    const queryKey = [QUERY_KEYS.TICKET, ticketId]

    try {
      // staleTime 0 overrides the 5-minute default so a planted or fresh cache still loads.
      await queryClient.fetchQuery({
        queryKey,
        staleTime: 0,
        queryFn: (ctx) => ticketQueryFn({ ...ctx, queryClient }),
      })
      log.info('[FCM] Fetched ticket data for:', ticketId)
    } catch (err) {
      log.error('[FCM] Failed to fetch ticket data:', err)
    }
  }

  //============== Approval handlers ==============
  function setApprovalInCache(approvalData) {
    if (!approvalData?.approvalId) return
    const approvalId = approvalData.approvalId
    queryClient.setQueryData([QUERY_KEYS.APPROVAL, approvalId], approvalData)
    const accountData = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (accountData?.approvals) {
      const exists = accountData.approvals.some((a) => a.approvalId === approvalId)
      accountStore.updateAccountApprovals(
        exists ? CACHE_ACTIONS.UPDATE : CACHE_ACTIONS.ADD,
        approvalData
      )
    }
  }

  function removeApprovalDetailQueries(approvalId) {
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.APPROVAL, approvalId] })
  }

  function removeApprovalFromCache(approvalId) {
    if (!approvalId) return
    removeApprovalDetailQueries(approvalId)
    accountStore.updateAccountApprovals(CACHE_ACTIONS.REMOVE, { approvalId })
  }

  async function fetchApprovalData(approvalId) {
    const queryKey = [QUERY_KEYS.APPROVAL, approvalId]

    try {
      // staleTime 0 overrides the 5-minute default so a planted or fresh cache still loads.
      await queryClient.fetchQuery({
        queryKey,
        staleTime: 0,
        queryFn: approvalQueryFn,
      })
      log.info('[FCM] Fetched approval data for:', approvalId)
    } catch (err) {
      log.error('[FCM] Failed to fetch approval data:', err)
    }
  }

  //============== Account data handlers ==============
  function setAccountDataInCache(accountData) {
    if (!accountData) return
    const hasQueryData = !!queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (!hasQueryData) {
      log.info('[FCM] Account data not set in cache - no query data')
      return
    }
    queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], accountData)
    log.info('[FCM] Account data set in cache')
  }

  function setCompaniesDirectoryInCache(updatedCompanies) {
    if (!updatedCompanies) return
    const hasQueryData = !!queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (!hasQueryData) {
      log.info('[FCM] Companies directory not set in cache - no query data')
      return
    }
    companyDictionary.addCompaniesToDirectory(updatedCompanies)
    log.info('[FCM] Companies directory set in cache')
  }

  // Detail caches only. The account refetch or broadcast payload replaces the lists.
  function applyPendingDetailQueries() {
    const deletedTicketIds = new Set(fcmPending.getIdsByPrefix(FCM_PENDING_PREFIXES.DELETE_TICKET))
    deletedTicketIds.forEach((ticketId) => {
      closeOpenTicketDrawer(ticketId)
      removeTicketDetailQueries(ticketId)
    })
    fcmPending.getIdsByPrefix(FCM_PENDING_PREFIXES.TICKET).forEach((ticketId) => {
      if (deletedTicketIds.has(ticketId)) return
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TICKET, ticketId],
        refetchType: 'none',
      })
    })

    const deletedApprovalIds = new Set(fcmPending.getIdsByPrefix(FCM_PENDING_PREFIXES.DELETE_AUTH))
    deletedApprovalIds.forEach((approvalId) => {
      closeOpenApprovalModal(approvalId)
      removeApprovalDetailQueries(approvalId)
    })
    fcmPending.getIdsByPrefix(FCM_PENDING_PREFIXES.APPROVAL).forEach((approvalId) => {
      if (deletedApprovalIds.has(approvalId)) return
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.APPROVAL, approvalId],
        refetchType: 'none',
      })
    })
    log.info('[FCM] Applied pending ticket and approval detail queries')
  }

  function closeOpenTicketDrawer(ticketId) {
    if (!ticketId) return
    const route = router.currentRoute.value
    if (route.query.ticketId !== ticketId) return
    const query = { ...route.query }
    delete query.ticketId
    delete query.tab
    router.replace({ query })
  }

  function closeOpenApprovalModal(approvalId) {
    if (!approvalId) return
    const route = router.currentRoute.value
    if (route.query.approvalId !== approvalId) return
    const query = { ...route.query }
    delete query.approvalId
    router.replace({ query })
  }

  return {
    addMessageToCache,
    removeMessageFromCache,
    fetchTicketData,
    fetchApprovalData,
    setTicketInCache,
    removeTicketFromCache,
    closeOpenTicketDrawer,
    closeOpenApprovalModal,
    setApprovalInCache,
    removeApprovalFromCache,
    setAccountDataInCache,
    applyPendingDetailQueries,
    setCompaniesDirectoryInCache,
  }
}
